import {
  Button,
  Card,
  CardContent,
  colors,
  Divider,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useAlert } from "react-alert";
import { useHotkeys } from "react-hotkeys-hook";
import PerfectScrollbar from "react-perfect-scrollbar";

import { areAllRequiredPropertiesAllowed } from "../destinations/DestinationPropertyMetadata";
import { RequestableSaasDestination } from "../destinations/destinations";
import { destinationIsValidForSource } from "../destinations/validation";
import { CannyRedirect } from "../destinations/modals/Canny";
import { DisableDestinationsModal } from "../destinations/modals/DisabledDestinationModal";
import useCurrentProject from "../hooks/use-project";
import useAccountMetadata from "../hooks/useAccountMetadata";
import Label from "../Label";
import { isAccountInHIPAAMode, makeRequest } from "../util";
import { HIPAAModeLabel } from "./HIPAAModeLabel";
import { HIPAAModeTooltip } from "./HIPAAModeTooltip";

export const useDestinationListStyles = makeStyles((theme) => ({
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  inner: {
    minWidth: 700,
  },
  switchContainerHidden: {
    visibility: "hidden",
  },
  card: {
    width: "85%",
    minWidth: 850,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  cardHeader: {
    padding: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  navigation: {
    paddingTop: 24,
  },
  clickableImg: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  configureButton: {
    "white-space": "nowrap",
    minWidth: "auto",
  },
  destinationInfo: {
    display: "flex",
    alignItems: "center",
  },
  destinationName: {
    color: "#444",
    fontSize: 16,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
    "white-space": "nowrap",
  },
  instanceName: {
    color: "#444",
    fontSize: 12,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
    "white-space": "nowrap",
  },
  tableCell: {
    borderBottom: "none",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  tableRowClickable: {
    "&:hover": {
      cursor: "pointer",
      "& button": {
        backgroundColor: "rgba(63, 81, 181, 0.08)",
      },
    },
  },
  searchField: {},

  tableSectionDivider: {
    background: "#e2e2e2",
  },
  tableSectionDividerInner: {
    paddingTop: 3,
    paddingBottom: 3,
  },
  noConfiguredDestinationInfo: {
    margin: "auto",
    "font-weight": "bold",
    textAlign: "center",
  },
}));

const isDestinationEnabled = (configuredDestinations, slug) => {
  return (
    (configuredDestinations[slug] && configuredDestinations[slug].enabled) ||
    false
  );
};

const DestinationListSearchInput = ({ initialSearch, onChange }) => {
  const classes = useDestinationListStyles();
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const inputRef = useRef();

  useEffect(() => {
    onChange(searchTerm);
  }, [searchTerm, onChange]);

  const setFocus = (e) => {
    e.preventDefault();

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  useHotkeys("ctrl+f", setFocus);
  useHotkeys("cmd+f", setFocus);

  return (
    <TextField
      variant="outlined"
      name="search"
      autoComplete="off"
      onChange={(e) => setSearchTerm(e.target.value)}
      value={searchTerm}
      inputRef={inputRef}
      InputProps={{
        className: clsx(classes.searchField),
        placeholder: "Search",
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon className={clsx(classes.icon)} />
          </InputAdornment>
        ),
      }}
    />
  );
};

DestinationListSearchInput.propTypes = {
  initialSearch: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function ConfigurableDestination({
  dest,
  configuredDestination,
  onChange,
  onConfigure,
}) {
  const classes = useDestinationListStyles();
  const accountMetadata = useAccountMetadata();
  const currentProject = useCurrentProject();
  const checked = configuredDestination?.enabled || false;

  const gotoConfigure = React.useCallback(
    () => onConfigure(dest, configuredDestination),
    [dest, configuredDestination, onConfigure]
  );

  if (!accountMetadata.loaded || !currentProject.loaded) {
    return null;
  }

  const requiredPropertiesBlocked =
    configuredDestination &&
    !areAllRequiredPropertiesAllowed(
      accountMetadata,
      currentProject,
      configuredDestination
    );
  const showToggle =
    configuredDestination &&
    (destinationIsValidForSource(configuredDestination, "client") ||
      destinationIsValidForSource(configuredDestination, "server")) &&
    !requiredPropertiesBlocked;

  const DestinationIcon = dest.logoSquare;

  return (
    <TableRow
      className={clsx(
        classes.tableRow,
        !showToggle && classes.tableRowClickable
      )}
      onClick={() => {
        if (!showToggle) {
          gotoConfigure();
        }
      }}
    >
      <TableCell
        className={clsx(classes.tableCell, "cursor-pointer")}
        onClick={() => {
          if (showToggle) {
            gotoConfigure();
          }
        }}
      >
        <div className={clsx(classes.destinationInfo)}>
          {DestinationIcon && <DestinationIcon height={34} width={34} />}
          <div>
            <div className={classes.destinationInfo}>
              <span className={classes.destinationName}>{dest.name}</span>
              {configuredDestination && (
                <HIPAAModeTooltip dest={configuredDestination} />
              )}
            </div>
            {dest.supportsMultiConfig && configuredDestination && (
              <div>
                <span className={classes.instanceName}>
                  {dest.multiConfigLabel}: {configuredDestination.instance_name}
                </span>
              </div>
            )}
          </div>
        </div>
      </TableCell>
      {isAccountInHIPAAMode(accountMetadata) && (
        <TableCell align="center" className={classes.tableCell}>
          {configuredDestination && (
            <HIPAAModeLabel dest={configuredDestination} />
          )}
        </TableCell>
      )}
      <TableCell align="center" className={classes.tableCell}>
        <div className={clsx(classes.switchContainer)}>
          {showToggle ? (
            <Switch
              checked={checked}
              color="secondary"
              edge="start"
              title={`${checked ? "Disable" : "Enable"} ${dest.name}`}
              name={
                "enable-destination-" + (showToggle ? "visible" : "invisible")
              }
              onChange={onChange}
              value={checked}
            />
          ) : (
            configuredDestination && (
              <Label color={colors.red[600]}>Invalid configuration</Label>
            )
          )}
        </div>
      </TableCell>
      <TableCell align="center" className={classes.tableCell}>
        <Button
          className={classes.configureButton}
          variant="outlined"
          color="primary"
          onClick={() => {
            if (showToggle) {
              // when showToggle = false then entire row is clickable
              // and click handler calls gotoConfigure
              // we don't want to call gotoConfigure to wrapped in this if block
              gotoConfigure();
            }
          }}
        >
          {showToggle ? "Manage" : "Configure"}
        </Button>
      </TableCell>
    </TableRow>
  );
}

const RequestableDestinationRow = ({ dest, onRequest }) => {
  const classes = useDestinationListStyles();
  const accountMetadata = useAccountMetadata();
  const DestinationIcon = dest.logoSquare;

  return (
    <TableRow className={clsx(classes.tableRow)} onClick={onRequest}>
      <TableCell className={clsx(classes.tableCell, "cursor-pointer")}>
        <div className={clsx(classes.destinationInfo)}>
          {DestinationIcon && <DestinationIcon height={34} width={34} />}
          <span className={classes.destinationName}>{dest.name}</span>
        </div>
      </TableCell>
      {isAccountInHIPAAMode(accountMetadata) && (
        <TableCell align="center" className={classes.tableCell}></TableCell>
      )}
      <TableCell align="center" className={classes.tableCell}></TableCell>
      <TableCell align="center" className={classes.tableCell}>
        <Button variant="outlined" color="primary">
          Request Integration
        </Button>
      </TableCell>
    </TableRow>
  );
};

const NoDestinationsConfiguredDestinationRow = ({ content }) => {
  const classes = useDestinationListStyles();

  return (
    <>
      <TableRow>
        <TableCell
          className={clsx(classes.tableCell, classes.tableSectionDividerInner)}
          // Need to span all columns. There will be 3 or 4 depending on whether
          // the account is in HIPAA mode.
          colSpan={4}
        >
          <div className={classes.noConfiguredDestinationInfo}>{content}</div>
        </TableCell>
      </TableRow>
    </>
  );
};

const TableSection = ({ sectionHeader, destinations, getRow }) => {
  const classes = useDestinationListStyles();
  const accountMetadata = useAccountMetadata();

  if (destinations.length == 0) {
    return null;
  }

  return (
    <>
      {sectionHeader && (
        <TableRow className={classes.tableSectionDivider}>
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.tableSectionDividerInner
            )}
            colSpan={isAccountInHIPAAMode(accountMetadata) ? 4 : 3}
          >
            {sectionHeader}
          </TableCell>
        </TableRow>
      )}

      {destinations.map(getRow)}
    </>
  );
};

const TableSectionContainer = ({ sections, getRow }) => {
  let sectionsWithContent = sections.filter(
    (section) => section.destinations.length > 0
  );

  if (sectionsWithContent.length == 1) {
    // don't show header if there is a single section
    sectionsWithContent = [
      {
        header: null,
        destinations: sectionsWithContent[0].destinations,
      },
    ];
  }
  return (
    <>
      {sectionsWithContent.map(({ header, destinations }, n) => (
        <TableSection
          key={"section" + n}
          sectionHeader={header}
          destinations={destinations}
          getRow={getRow}
        />
      ))}
    </>
  );
};

const DestinationsList = ({
  destinations,
  configuredDestinations,
  onConfigure,
  onConfiguredDestsChange,
  emptyState,
}) => {
  const classes = useDestinationListStyles();
  const alert = useAlert();
  const accountMetadata = useAccountMetadata();

  const [disableModalDest, setDisableModalDest] = useState(null);
  const [requestModalDest, setRequestModalDest] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchTermChange = (term) => {
    setSearchTerm(term.trim());
  };

  let filteredDestinations = React.useMemo(
    () => _.sortBy([...destinations], (d) => d.name.toLowerCase()),
    [destinations]
  );

  if (searchTerm && searchTerm !== "") {
    filteredDestinations = _.filter(filteredDestinations, (dest) => {
      return (
        dest.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
        dest.key.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
      );
    });
  }

  // Turns on or off the given destination based on the state.
  function setDestinationState(config, state) {
    const id = config.id;

    makeRequest("toggle_whole_destination", { dest_id: id, state: state })
      .then(() => {
        const newDestState = _.extend({}, config, {
          enabled: state,
          id: id,
        });
        onConfiguredDestsChange({
          [id]: newDestState,
        });
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to toggle destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleToggleDestination(config) {
    const targetState = !(config?.enabled || false);
    if (targetState === false) {
      setDisableModalDest(config);
    } else {
      setDestinationState(config, true);
    }
  }

  const NoDestinationsConfigured = "NoDestinationsConfigured";

  const getRow = (dest) => {
    if (dest === NoDestinationsConfigured) {
      return (
        <NoDestinationsConfiguredDestinationRow
          key={NoDestinationsConfigured}
          content={emptyState}
        />
      );
    }

    // In this context the destination is the SaasDestination or such that
    // describes a destination that could be configured (even if it's not yet
    // supported.) config describes the the current configuration this row is
    // representing if one exists.
    const { dest: destination, config } = dest;

    // SaasDestination && WarehouseDestination
    if (!(destination instanceof RequestableSaasDestination)) {
      return (
        <ConfigurableDestination
          key={destination.slug + ":" + (config?.id || "")}
          onConfigure={onConfigure}
          dest={destination}
          configuredDestination={config}
          onChange={() => handleToggleDestination(config)}
        />
      );
    }

    if (searchTerm) {
      // Only show requestable destinations during search
      return (
        <RequestableDestinationRow
          dest={destination}
          key={destination.slug}
          onRequest={() => {
            CannyRedirect();
          }}
        />
      );
    }
    return null;
  };

  const enabledDestinations = [];
  const disabledDestinations = [];
  const appDestinations = [];
  const adDestinations = [];
  const functionDestinations = [];

  function getListForDestinationConfig(dest, config) {
    if (config) {
      if (config.enabled) {
        return enabledDestinations;
      } else {
        return disabledDestinations;
      }
    } else if (dest.type === "advertising") {
      return adDestinations;
    } else if (dest.type === "function") {
      return functionDestinations;
    } else {
      return appDestinations;
    }
  }

  for (let dest of filteredDestinations) {
    const configs = configuredDestinations.filter(
      (config) => config.type_slug == dest.slug
    );

    if (configs.length > 0) {
      for (let x in configs) {
        getListForDestinationConfig(dest, configs[x]).push({
          dest: dest,
          config: configs[x],
        });
      }

      // If there are multiple configs for this destination, add a null config to the end of the appropriate list
      if (dest.supportsMultiConfig) {
        getListForDestinationConfig(dest, null).push({
          dest: dest,
          config: null,
        });
      }
    } else {
      getListForDestinationConfig(dest, null).push({
        dest: dest,
        config: null,
      });
    }
  }

  if (!searchTerm && enabledDestinations.length == 0) {
    enabledDestinations.push(NoDestinationsConfigured);
  }

  const sections = [
    {
      header: "Active Destinations",
      destinations: enabledDestinations,
    },
    {
      header: "Inactive Destinations",
      destinations: disabledDestinations, // destinations that are configured but not enabled
    },
    {
      header: "Apps Destinations",
      destinations: appDestinations,
    },
    {
      header: "Advertising Destinations",
      destinations: adDestinations,
    },
    {
      header: "Function Destinations",
      destinations: functionDestinations,
    },
  ];

  return (
    <>
      <Card className={clsx(classes.card)}>
        <CardContent className={classes.content}>
          <div key="search-wrapper" className={classes.cardHeader}>
            <h4>Destinations</h4>
            <DestinationListSearchInput
              initialSearch={searchTerm}
              key="search"
              onChange={handleSearchTermChange}
            />
          </div>

          <Divider />
          <PerfectScrollbar>
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Integration</TableCell>
                    {isAccountInHIPAAMode(accountMetadata) && (
                      <TableCell align="center">HIPAA</TableCell>
                    )}
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Settings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableSectionContainer sections={sections} getRow={getRow} />
                  {filteredDestinations.length === 0 &&
                    searchTerm &&
                    searchTerm !== "" && (
                      <TableRow
                        className={clsx(classes.tableRow)}
                        onClick={CannyRedirect}
                      >
                        <TableCell colSpan={2}>
                          No destinations found for &quot;{searchTerm}&quot;
                        </TableCell>
                        <TableCell className={classes.tableCell} />
                        <TableCell align="center" className={classes.tableCell}>
                          <Button variant="outlined" color="primary">
                            Request Integration
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
      </Card>
      <DisableDestinationsModal
        open={disableModalDest !== null}
        onClose={() => setDisableModalDest(null)}
        onConfirm={() => setDestinationState(disableModalDest, false)}
        dest={disableModalDest}
      />
    </>
  );
};

DestinationsList.propTypes = {
  destinations: PropTypes.array.isRequired,
  configuredDestinations: PropTypes.array.isRequired,
  onConfigure: PropTypes.func.isRequired,
  onConfiguredDestsChange: PropTypes.func.isRequired,
  emptyState: PropTypes.node,
};

export default DestinationsList;
